import {
  useState,
  useCallback,
  useMemo,
  FocusEvent,
  FC,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '__components/Inputs/TextInput';
import { useFlashTransition } from '__hooks/useFlashState';
import { usePreserveCaretPosition } from '__hooks/usePreserveCaretPosition';

const MAX_API_NAME_LENGTH = 100;
const MIN_API_NAME_LENGTH = 2;

type ApiNameInputProps = {
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
  label: string;
  placeholder: string;
};

export const ApiNameInput: FC<ApiNameInputProps> = ({
  value,
  onChange,
  error,
  setError,
  label,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const [name, setName] = useState(value);

  useEffect(() => {
    if (error) {
      flashErrorMessage(error);
    } else {
      cancelFlash();
    }
  }, [error, flashErrorMessage, cancelFlash]);

  const handleNameBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => {
      const trimmedValue = value.trim();
      onChange(trimmedValue);
      if (!trimmedValue) {
        setError(t('API name may not be blank.'));
      } else if (trimmedValue.length > MAX_API_NAME_LENGTH) {
        setError(
          t('Maximum field size is {{maxLength}} characters.', {
            maxLength: MAX_API_NAME_LENGTH,
          })
        );
      } else if (trimmedValue.length < MIN_API_NAME_LENGTH) {
        setError(
          t('Minimum field size is {{minLength}} characters.', {
            minLength: MIN_API_NAME_LENGTH,
          })
        );
      } else if (!/^[a-z_][a-z0-9_]+$/.test(trimmedValue)) {
        setError(
          t(
            'Must start with a letter or underscore and contain only lowercase letters, numbers, or underscores.'
          )
        );
      }
    },
    [onChange, t, setError]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setError(null);
      const croppedName = value
        .substring(0, MAX_API_NAME_LENGTH)
        .toLowerCase()
        .replaceAll(' ', '_');

      setName(croppedName);
    },
    [setError]
  );

  const validateApiName = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  const inputRef = usePreserveCaretPosition(name);

  return (
    <TextInput
      inputRef={inputRef}
      label={label}
      placeholder={placeholder}
      labelInfo={t('Only letters, numbers, and _ are allowed.')}
      labelInfoPlacement="top"
      value={name}
      onChange={handleNameChange}
      onBlur={handleNameBlur}
      validate={validateApiName}
      error={!!error}
      data-qa="api-name-input"
    />
  );
};
